import React, { useState, useCallback } from "react";
import { Modal, Form, Select, DatePicker, Button, message } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useLocation } from "context/LocationContext";
import { generateReport, ReportType } from "./reportGenerators";
import { useNotifications } from "../../context/NotificationsContext";

const { RangePicker } = DatePicker;

interface FormValues {
    reportType: "reservation" | "checkmatch" | "checkmatch_total";
    dateRange: [Dayjs, Dayjs];
    partnerVenueIds?: string[];
}

export interface GeneratePayload {
    reportType: "reservation" | "checkmatch" | "checkmatch_total";
    startDate: string;
    endDate: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function ReportModal({ open, onClose }: Props) {
    const [form] = Form.useForm<FormValues>();
    const [loading, setLoading] = useState(false);
    const apiClient = useApi();
    const { accessToken } = useAuth();
    const { locations } = useLocation();


// Filter only locations with checkMatchEnabled true
    const filteredLocations = locations?.filter(
        (loc: any) => loc.checkMatchEnabled === true
    ) ?? [];


    const { notify } = useNotifications();

    const handleClose = useCallback(() => {
        form.resetFields();
        onClose();
    }, [form, onClose]);


    const handleGenerateClick = useCallback(async (): Promise<void> => {
        try {
            if (!accessToken) {
                throw new Error("Missing auth token.");
            }
            const partnerVenueIds = form.getFieldValue("partnerVenueIds");

            if (!partnerVenueIds) {
                throw new Error("Missing partner venue id/s.");
            }

            const { reportType, dateRange } = await form.validateFields();

            if (!Array.isArray(dateRange) || dateRange.length !== 2) {
                throw new Error("Please select a valid date range.");
            }

            const [start, end] = dateRange;

            setLoading(true);
            await generateReport(reportType as ReportType, {
              start,
              end,
              partnerVenueIds,
              accessToken,
              apiClient,
              notify
            });
            handleClose();
        } catch (err: any) {
            void message.error(err?.message ?? "Failed to generate/download report.");
        } finally {
            setLoading(false);
        }
    }, [form, accessToken, apiClient, notify, handleClose]);

    return (
      <Modal
        title="Generate Report"
        open={open}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          name="reportForm"
          autoComplete="off"
          initialValues={{
            reportType: "Please select a report",
            dateRange: [dayjs().startOf("month"), dayjs().endOf("month")],
            partnerVenueNames: [],
          }}
        >
          <Form.Item
            name="reportType"
            label="Report"
            rules={[{ required: true, message: "Please select a report type" }]}
          >
            <Select
              data-testid ="reportType"
              options={[
                { value: "reservation", label: "CHECKMATCH RESERVATIONS" },
                { value: "checkmatch", label: "CHECKMATCH MATCHES" },
                { value: "checkmatch_total", label: "CHECKMATCH TOTAL" }// future
              ]}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.reportType !== cur.reportType}>
            <Form.Item
              name="partnerVenueIds"
              label="Location(s)"
              rules={[{ required: true, message: "Please select at least one location" }]}
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                style={{ width: "100%" }}
                placeholder="Select locations"
                filterOption={(input, option) => {
                  const label = typeof option?.children === "string" ? option.children : "";
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
              >

                {(filteredLocations ?? []).filter(l => !!l.partnerVenueId)
                  .map(l => (
                                    <Select.Option
                                      key={String(l.locationId)}
                                      value={l.partnerVenueId}>
                                      {l.name}
                                    </Select.Option>
                                ))}
              </Select>
            </Form.Item>
          </Form.Item>


          <Form.Item
            dependencies={["reportType"]}
            name="dateRange"
            label="Date range"
            rules={[
              { required: true, message: "Please select a date range." },
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {

                    const isReservation = getFieldValue("reportType") === "reservation";

                    if (!isReservation) return await Promise.resolve();
                    if (!value || value.length !== 2) {
                                    return await Promise.reject(new Error("Please select a valid date range."));
                                }
                                const [start, end] = value;
                                const diffDays = Number(end.diff(start, "day")) + 1;
                                const MAX_DAYS = 15;
                                return diffDays > MAX_DAYS
                                  ? await Promise.reject(
                                    new Error(`Date range cannot exceed ${MAX_DAYS} days (inclusive).`)
                                  )
                                  : await Promise.resolve();
                            },
                }),
            ]}
          >
            <RangePicker style={{ width: "100%" }} format="YYYY-MM-DD"/>
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Button
              type="primary"
              loading={loading}
              onClick={() => {
                            void handleGenerateClick();
                        }}
            >
                        Generate Report
            </Button>
            <Button
              role="modal-close"
              onClick={handleClose} disabled={loading}>
                        Close
            </Button>
          </div>
        </Form>
      </Modal>
    );
}
